import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "sonner";

import { IAPIResponse } from "@/services/api.types";
import { API_BASE_URL } from "@/config/env.config";
import { AUTH_TOKEN_STORAGE_KEY } from "@/constant/auth.constant";

export { API_BASE_URL } from "@/config/env.config";

export const apiInstance: AxiosInstance = axios.create({ baseURL: API_BASE_URL });

apiInstance.interceptors.request.use(config => {
  const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function isApiErrorHandled(error: unknown): boolean {
  return (error as { stoopToastHandled?: boolean } | null)?.stoopToastHandled === true;
}

export function apiErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<Partial<IAPIResponse<unknown>> & { detail?: unknown }>;
  const body = axiosError.response?.data;
  if (typeof body?.message === "string" && body.message.trim()) return body.message;
  if (typeof body?.detail === "string" && body.detail.trim()) return body.detail;
  if (body?.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
    const detailMessage = (body.detail as { message?: unknown }).message;
    if (typeof detailMessage === "string" && detailMessage.trim()) return detailMessage;
  }
  if (Array.isArray(body?.detail)) {
    const validationMessage = body.detail.find(
      (item): item is { msg: string } => typeof item?.msg === "string" && Boolean(item.msg.trim()),
    )?.msg;
    if (validationMessage) return validationMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong";
}

apiInstance.interceptors.response.use(
  response => response,
  (err: AxiosError<IAPIResponse<unknown>>) => {
    const status = err?.response?.status;
    const body = err?.response?.data;
    const errorData = body?.data && typeof body.data === "object"
      ? body.data as Record<string, unknown>
      : null;
    let handled = false;

    // 402 = the org's trial lapsed or its subscription is past due, so the
    // server refuses every write. This used to hard-navigate to the billing
    // page, which discarded whatever the user was in the middle of typing and
    // gave no reason for the jump — it read as a crash. Explain it where they
    // are instead and let them choose to go; the app-wide banner
    // (TrialBanner) is already pointing at billing the whole time.
    if (status === 402) {
      const slug = typeof errorData?.org_slug === "string" ? errorData.org_slug : null;
      const onBillingPage = window.location.pathname.endsWith("/billing");
      if (!onBillingPage) {
        toast.error(apiErrorMessage(err), {
          description: "Your existing deals keep working — you can still update tasks, upload documents and message clients.",
          action: slug
            ? { label: "Upgrade", onClick: () => { window.location.href = `/o/${slug}/billing`; } }
            : undefined,
          duration: 10000,
        });
      }
      handled = true;
    }
    if (status === 409 && (errorData?.code === "plan_limit_deals" || errorData?.code === "plan_limit_seats" || errorData?.code === "plan_limit_storage")) {
      const slug = typeof errorData.org_slug === "string" ? errorData.org_slug : null;
      // A hit limit now has two ways out — buy one more unit, or move up a
      // tier — and "Upgrade" only named the expensive one. Sonner takes a
      // single action, so the button points at the add-ons card when an add-on
      // would resolve the block; the billing page shows both choices together
      // anyway, so nobody is steered away from upgrading.
      const canAddOn = errorData.can_add_on === true;
      const target = canAddOn ? `/o/${slug}/billing#addons` : `/o/${slug}/billing`;
      toast.error(apiErrorMessage(err), {
        action: slug
          ? {
              label: canAddOn ? "Add more" : "Upgrade",
              onClick: () => { window.location.href = target; },
            }
          : undefined,
        duration: 8000,
      });
      handled = true;
    }
    if (status === 429 && errorData?.code === "rate_limited") {
      toast.error(apiErrorMessage(err), { duration: 6000 });
      handled = true;
    }

    const message = apiErrorMessage(err);
    const rejected = new Error(message) as Error & { stoopToastHandled?: boolean };
    if (handled) rejected.stoopToastHandled = true;
    return Promise.reject(rejected);
  }
);

export class API {
  private static handleResponse<RES>(
    response: AxiosResponse<IAPIResponse<RES>>
  ): IAPIResponse<RES> {
    return {
      data: response.data?.data ?? null,
      status: response.data?.status ?? false,
      message: response.data?.message ?? "",
      meta: response.data?.meta,
    };
  }

  private static handleError(error: unknown): never {
    if (isApiErrorHandled(error)) throw error;
    throw new Error(apiErrorMessage(error));
  }

  static async Get<RES>(url: string, config?: AxiosRequestConfig): Promise<IAPIResponse<RES>> {
    try {
      return this.handleResponse(await apiInstance.get<IAPIResponse<RES>>(url, config));
    } catch (error) {
      this.handleError(error);
    }
  }

  static async Post<REQ, RES>(url: string, payload?: REQ, config?: AxiosRequestConfig): Promise<IAPIResponse<RES>> {
    try {
      return this.handleResponse(await apiInstance.post<IAPIResponse<RES>>(url, payload, config));
    } catch (error) {
      this.handleError(error);
    }
  }

  static async Put<REQ, RES>(url: string, payload?: REQ, config?: AxiosRequestConfig): Promise<IAPIResponse<RES>> {
    try {
      return this.handleResponse(await apiInstance.put<IAPIResponse<RES>>(url, payload, config));
    } catch (error) {
      this.handleError(error);
    }
  }

  static async Patch<REQ, RES>(url: string, payload?: REQ, config?: AxiosRequestConfig): Promise<IAPIResponse<RES>> {
    try {
      return this.handleResponse(await apiInstance.patch<IAPIResponse<RES>>(url, payload, config));
    } catch (error) {
      this.handleError(error);
    }
  }

  static async Delete<REQ, RES>(url: string, data?: REQ, config?: AxiosRequestConfig): Promise<IAPIResponse<RES>> {
    try {
      return this.handleResponse(await apiInstance.delete<IAPIResponse<RES>>(url, { ...config, data }));
    } catch (error) {
      this.handleError(error);
    }
  }
}
